import { MNSproCloudApi } from '..';
import {
  DriveItem,
  EducationAssignmentResource,
  EducationAssignmentResponse,
  EducationSubmissionResource,
  HomeworkResponse,
} from '../api/v2';

const DATA_AS_OF_HEADER = 'x-data-as-of';

export interface AssignmentsWithDataAsOf {
  assignments: EducationAssignmentResponse[];
  dataAsOf?: string;
}

/**
 * Service class which defines methods to be used for MNSpro Cloud Organization management.
 *
 * @class Organization
 */
class AssignmentsService {
  /**
   *
   *
   * @static
   * @param {Guid} groupId GUID should be empty if you want all assignments
   * @param {boolean} singleCourseMode should be true if you want only one course
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<EducationAssignmentsResponse[]>}
   * @memberof AssignmentsService
   */
  static async getAll(
    groupId: string,
    singleCourseMode: boolean,
    authToken: string,
  ): Promise<EducationAssignmentResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const result = await api.getAllAssignmentFromClass(
      groupId,
      singleCourseMode,
    );
    return result.data;
  }

  /**
   * Like `getAll`, but also returns `dataAsOf` (ISO 8601) when the backend could not reach
   * Microsoft and answered with the listing cached at that time (response header `X-Data-As-Of`).
   * `bypassCache` sends `Cache-Control: no-cache`, so the backend asks Microsoft even if it holds a
   * recent listing; while Microsoft throttles, the cached listing is still returned.
   */
  static async getAllWithDataAsOf(
    authToken: string,
    groupId: string,
    singleCourseMode: boolean,
    bypassCache = false,
  ): Promise<AssignmentsWithDataAsOf> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const result = await api.getAllAssignmentFromClass(
      groupId,
      singleCourseMode,
      undefined,
      bypassCache ?{ headers: { 'Cache-Control': 'no-cache' } } : undefined,
    );
    const dataAsOf: unknown = result.headers[DATA_AS_OF_HEADER];

    return {
      assignments: result.data,
      dataAsOf: typeof dataAsOf === 'string' ? dataAsOf : undefined,
    };
  }

  /**
   *
   *
   * @static
   * @param {Guid} groupId GUID should be empty if you want all assignments
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<EducationAssignmentsResponse[]>}
   * @memberof AssignmentsService
   */
  static async createAssignmentInDraftState(
    groupId: string,
    authToken: string,
  ): Promise<EducationAssignmentResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const result = await api.createEducationAssignmentForClass({ groupId });
    return result.data;
  }

  /**
   *
   *
   * @static
   * @param {Guid} groupId GUID should be empty if you want all assignments
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<EducationAssignmentsResponse[]>}
   * @memberof AssignmentsService
   */
  static async updateEducationAssignment(
    groupId: string,
    assignment: EducationAssignmentResponse,
    authToken: string,
  ): Promise<EducationAssignmentResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const result = await api.updateAssignmentFromClass({
      eduAssignment: {
        ...assignment,
        grading: {
          ...assignment.grading,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          /* @ts-ignore: Issue with generated Api */
          '@odata.type': '#microsoft.graph.educationAssignmentPointsGradeType',
        },
      },
      groupId: groupId,
    });
    return result.data;
  }

  /**
   *
   *
   * @static
   * @param {Guid} groupId ClassID of Assignment
   * @param {Guid} assignmentId ID of the Assignment
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<bool>}
   * @memberof AssignmentsService
   */
  static async publishEducationAssignment(
    groupId: string,
    assignmentId: string,
    authToken: string,
  ): Promise<boolean> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const result = await api.publishAssignmentForClass(groupId, assignmentId);
    return result.status === 200;
  }

  /**
   *
   *
   * @static
   * @param {Guid} assignmentId ID of the Assignment
   * @param {Guid} groupId ClassID of Assignment
   * @param {Guid} submissionId SubmissionId of Assignment
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<bool>}
   * @memberof AssignmentsService
   */
  static async sendStudentSubmission(
    groupId: string,
    assignmentId: string,
    submissionId: string,
    authToken: string,
  ): Promise<boolean> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const result = await api.sendSubmissionForClass(
      groupId,
      assignmentId,
      submissionId,
    );
    return result.status === 200;
  }

  /**
   *
   *
   * @static
   * @param {Guid} groupId ClassID of Assignment
   * @param {Guid} assignmentId ID of the Assignment
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<void>}
   * @memberof AssignmentsService
   */
  static async deleteEducationAssignment(
    groupId: string,
    assignmentId: string,
    authToken: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    await api.deleteAssignmentFromClass(groupId, assignmentId);
  }

  /**
   * @param {Guid} groupId ClassId of Assignment
   * @param {Guid} assignmentId  Id of the assignment
   * @param {EducationAssignmentResponse} assignmentResource assignmentResource should be added
   * @param {DriveItem} sourceFile file
   * @param authToken JWT (without 'Bearer'-prefix)
   * @return EducationAssignmentResource
   * @memberof AssignmentsService
   */
  static async addAssignmentResource(
    groupId: string,
    assignmentId: string,
    assignmentResource: EducationAssignmentResource,
    sourceFile: DriveItem,
    authToken: string,
  ): Promise<EducationAssignmentResource> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const result = await api.addResourceToAssignment({
      assignmentId,
      groupId,
      assignmentResource,
    });

    return result.data;
  }

  /**
   *
   * @param {Guid} groupId ClassId of Assignment
   * @param {Guid} assignmentId  Id of the assignment
   * @param {Guid} submissionId submission id from student
   * @param {EducationAssignmentResponse} assignmentResource assignmentResource should be added
   * @param {DriveItem} sourceFile file
   * @param authToken JWT (without 'Bearer'-prefix)
   * @return EducationAssignmentResource
   * @memberof AssignmentsService
   */
  static async addSubmissionResource(
    groupId: string,
    assignmentId: string,
    submissionId: string,
    assignmentResource: EducationAssignmentResource,
    sourceFile: DriveItem,
    authToken: string,
  ): Promise<EducationSubmissionResource> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const result = await api.addSubmissionResourceForClass({
      assignmentId,
      groupId,
      sourceFile,
      submissionId,
      assignmentResource,
    });

    return result.data;
  }

  /**
   *
   * @param {Guid} groupId ClassId of Assignment
   * @param {Guid} assignmentId  Id of the assignment
   * @param {Guid} resourceId submission id from student
   * @param {EducationAssignmentResponse} assignmentResource assignmentResource should be added
   * @param {DriveItem} sourceFile file
   * @param authToken JWT (without 'Bearer'-prefix)
   * @return EducationAssignmentResource
   * @memberof AssignmentsService
   */
  static async updateAssignmentRecourceFromClass(
    groupId: string,
    assignmentId: string,
    resourceId: string,
    assignmentResource: EducationAssignmentResource,
    authToken: string,
  ): Promise<EducationAssignmentResource> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const result = await api.updateAssignmentRecourceFromClass(
      groupId,
      assignmentId,
      resourceId,
      assignmentResource,
    );

    return result.data;
  }

  /**
   *
   * @param {Guid} groupId ClassId of Assignment
   * @param {Guid} assignmentId  Id of the assignment
   * @param {Guid} resourceId resourceId from student
   * @param {Guid} driveitemId Id of driveItem -> Resource
   * @param authToken JWT (without 'Bearer'-prefix)
   * @return EducationAssignmentResource
   * @memberof AssignmentsService
   */
  static async deleteAssignmentResource(
    groupId: string,
    assignmentId: string,
    resourceId: string,
    driveItemId: string,
    authToken: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    await api.deleteAssignmentResourceFromClass(
      groupId,
      assignmentId,
      resourceId,
      driveItemId,
    );
  }

  /**
   *
   * @param {Guid} groupId ClassId of Assignment
   * @param {Guid} assignmentId  Id of the assignment
   * @param {Guid} resourceId resourceId
   * @param {Guid} submissionId id from student
   * @param {Guid} driveitemId Id of driveItem -> Resource
   * @param authToken JWT (without 'Bearer'-prefix)
   * @return EducationAssignmentResource
   * @memberof AssignmentsService
   */
  static async deleteSubmissionResource(
    groupId: string,
    assignmentId: string,
    resourceId: string,
    submissionId: string,
    driveItemId: string,
    authToken: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    await api.deleteSubmissionResourceFromClass(
      groupId,
      assignmentId,
      resourceId,
      submissionId,
      driveItemId,
    );
  }

  /**
   *
   * @param {Guid} groupId ClassId of Assignment
   * @param {Guid} assignmentId  Id of the assignment
   * @param {Guid} resourceId resourceId
   * @param authToken JWT (without 'Bearer'-prefix)
   * @return EducationAssignmentResource
   * @memberof AssignmentsService
   */
  static async deleteAssignmentLinkResource(
    groupId: string,
    assignmentId: string,
    resourceId: string,
    authToken: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    await api.deleteLinkResourceFromClass(groupId, assignmentId, resourceId);
  }

  static async getMyHomework(
    authToken: string,
    organizationId: string,
    onlyClassbook?: boolean,
  ): Promise<HomeworkResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.EducationAssignmentsApiFactory(config);

    const response = await api.getMyHomework(
      organizationId,
      undefined,
      onlyClassbook,
    );

    return response.data;
  }
}

export { AssignmentsService };
